import {
    NodeOutputSensorDataConfigSlotCode,
    OutputSensorDataConfigSdResponse,
} from '@kcftech/prometheus-client';
import { sdClient } from 'contexts/SDKClientContext';
import {
    BaseDataSourceOptions,
    DataSourceQuery,
} from 'custom_hooks/data_hooks/data_source/data_source';
import {
    InfiniteDataSource,
    useInfiniteDataSource,
} from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import {
    PagedDataSource,
    usePagedDataSource,
} from 'custom_hooks/data_hooks/data_source/usePagedDataSource';
import { queryClient } from 'utils/query';
import { buildOrderBy, Filter, toDataSourceResult } from 'utils/query_v3';
import { OptionalProps } from 'utils/types';

export const ThirdPartySensorConfigsDataSourceKey = 'third-party-sensor-configs';

export type ConfigsFilter = Readonly<{
    sensorModelId?: string;
    slotCode?: NodeOutputSensorDataConfigSlotCode;
    corporationId?: string;
    isApproved?: boolean;
}>;

export type UseThirdPartySensorConfigsDataSourceOptions = OptionalProps<
    BaseDataSourceOptions<OutputSensorDataConfigSdResponse, ConfigsFilter>,
    'name'
>;

export type ThirdPartySensorConfigsPagedDataSource = PagedDataSource<
    OutputSensorDataConfigSdResponse,
    ConfigsFilter
>;

export type ThirdPartySensorConfigsInfiniteDataSource = InfiniteDataSource<
    OutputSensorDataConfigSdResponse,
    ConfigsFilter
>;

const getAllThirdPartySensorConfigs: DataSourceQuery<
    OutputSensorDataConfigSdResponse,
    ConfigsFilter
> = ({
    filter: { sensorModelId, slotCode, corporationId, isApproved },
    sort,
    page,
    pageLimit,
}) => {
    const filterExpr = Filter.and(
        sensorModelId !== undefined
            ? Filter.eq('PhysicalSensorModel.Id', sensorModelId)
            : undefined,
        Filter.or(
            corporationId
                ? Filter.eq('Corporation.CorporationId', corporationId)
                : undefined,
            isApproved ? Filter.isNull('Corporation') : undefined,
        ),
        isApproved === false ? Filter.isNotNull('Corporation') : undefined,
    );
    const orderBy = buildOrderBy(sort);

    return toDataSourceResult(
        sdClient.sdGetOutputSensorDataConfigs({
            filter: filterExpr,
            page,
            pageLimit,
            orderBy,
            slotCode,
        }),
    );
};

export function usePagedThirdPartySensorConfigsDataSource({
    ...options
}: UseThirdPartySensorConfigsDataSourceOptions): ThirdPartySensorConfigsPagedDataSource {
    return usePagedDataSource({
        name: ThirdPartySensorConfigsDataSourceKey,
        ...options,
        query: getAllThirdPartySensorConfigs,
    });
}

export function useInfiniteThirdPartySensorConfigsDataSource({
    ...options
}: UseThirdPartySensorConfigsDataSourceOptions): ThirdPartySensorConfigsInfiniteDataSource {
    return useInfiniteDataSource({
        name: ThirdPartySensorConfigsDataSourceKey,
        ...options,
        query: getAllThirdPartySensorConfigs,
    });
}

export function invalidateThirdPartySensorConfigsDataSource() {
    queryClient.invalidateQueries({
        queryKey: [ThirdPartySensorConfigsDataSourceKey],
    });
}
